import React from 'react';
import * as XLSX from 'xlsx';
import { ArrowLeft, CheckCircle, Lock, Trash2, AlertTriangle, Download, Clock, XCircle, RotateCcw, Calculator, ChevronRight } from 'lucide-react';
import ResumenTab   from './ResumenTab';
import EmpleadosTab from './EmpleadosTab';
import FormulasTab  from './FormulasTab';

const ESTADO_CONFIG = {
  borrador: { label: 'Borrador',  cls: 'bg-amber-100 text-amber-700',  Icon: Clock       },
  aprobado: { label: 'Aprobado',  cls: 'bg-blue-100 text-blue-700',    Icon: CheckCircle },
  pagado:   { label: 'Pagado',    cls: 'bg-green-100 text-green-700',  Icon: Lock        },
  cancelado:{ label: 'Cancelado', cls: 'bg-red-100 text-red-600',      Icon: XCircle     },
};

const TIPO_LABELS = { LEGAL: 'Legal', FIJA: 'Fija', MIXTA: 'Mixta' };

const n   = (v) => parseFloat(v || 0);
const fmt = (v) => n(v).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function exportXLSX(bonificacion, detalle, tasaInfotep) {
  const tipo       = TIPO_LABELS[bonificacion.tipo] || bonificacion.tipo;
  const activos    = detalle.filter(d => d.estado !== 'excluido');
  const pctLabel   = `INFOTEP (${(tasaInfotep * 100).toFixed(2)}%)`;
  const conISR     = !!bonificacion.isr_calculado;

  const filas = activos.map(d => {
    const final   = n(d.monto_final);
    const infotep = final * tasaInfotep;
    const isrBono = conISR ? n(d.isr_monto) : 0;
    const neto    = conISR ? n(d.neto_a_pagar) : final - infotep;
    const row = {
      'Empleado':        d.apellidos_nombres,
      'Departamento':    d.departamento || '',
      'Salario Mensual': n(d.salario_mensual),
      'Años Serv.':      n(d.anos_servicio),
      'Meses Trab.':     n(d.dias_trabajados),
      'Bonif. Legal':    n(d.monto_legal),
      'Distribución':    n(d.monto_adicional),
      'Total':           n(d.monto_total),
      'Ajustado':        d.monto_ajustado != null ? n(d.monto_ajustado) : '',
      [pctLabel]:        infotep,
    };
    if (conISR) row['ISR a Retener'] = isrBono;
    row['Neto a Pagar'] = neto;
    row['Estado']       = d.estado;
    return row;
  });

  const totalInfotep = activos.reduce((s, d) => s + n(d.monto_final) * tasaInfotep, 0);
  const totalISR     = conISR ? activos.reduce((s, d) => s + n(d.isr_monto), 0) : 0;
  const totalNeto    = conISR
    ? activos.reduce((s, d) => s + n(d.neto_a_pagar), 0)
    : activos.reduce((s, d) => s + n(d.monto_final) * (1 - tasaInfotep), 0);

  const totRow = {
    'Empleado':        'TOTAL',
    'Departamento':    '',
    'Salario Mensual': '',
    'Años Serv.':      '',
    'Meses Trab.':     activos.length,
    'Bonif. Legal':    activos.reduce((s, d) => s + n(d.monto_legal), 0),
    'Distribución':    activos.reduce((s, d) => s + n(d.monto_adicional), 0),
    'Total':           activos.reduce((s, d) => s + n(d.monto_total), 0),
    'Ajustado':        '',
    [pctLabel]:        totalInfotep,
  };
  if (conISR) totRow['ISR a Retener'] = totalISR;
  totRow['Neto a Pagar'] = totalNeto;
  totRow['Estado']       = '';
  filas.push(totRow);

  const ws = XLSX.utils.aoa_to_sheet([]);
  XLSX.utils.sheet_add_aoa(ws, [
    [`Bonificación Anual — ${bonificacion.nombre}`],
    ['Año Fiscal', bonificacion.anio, 'Tipo', tipo],
    ['Total Empleados', bonificacion.total_empleados, 'Total Bonificación', n(bonificacion.total_bonificacion),
     ...(conISR ? ['Total ISR', n(bonificacion.total_isr)] : [])],
    [],
  ], { origin: 'A1' });
  XLSX.utils.sheet_add_json(ws, filas, { origin: 'A5', skipHeader: false });

  const numCols = conISR
    ? ['C','D','E','F','G','H','I','J','K','L']
    : ['C','D','E','F','G','H','I','J','K'];
  ws['!cols'] = [
    { wch: 30 }, { wch: 18 }, { wch: 16 }, { wch: 10 }, { wch: 10 },
    { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 },
    ...(conISR ? [{ wch: 16 }] : []),
    { wch: 16 }, { wch: 12 },
  ];

  const numFmt   = '#,##0.00';
  const dataStart = 6;
  const totalRows = activos.length + 1;
  for (let r = 0; r < totalRows; r++) {
    const row = dataStart + r;
    for (const col of numCols) {
      const addr = `${col}${row}`;
      if (ws[addr] && ws[addr].t === 'n') ws[addr].z = numFmt;
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `Bonif ${bonificacion.anio}`);
  XLSX.writeFile(wb, `Bonificacion_${bonificacion.anio}_${tipo}.xlsx`);
}

const STEPS = [
  { id: 'generado',      label: 'Generado'      },
  { id: 'isr_calculado', label: 'ISR Calculado'  },
  { id: 'aprobado',      label: 'Aprobado'       },
  { id: 'pagado',        label: 'Pagado'         },
];

function getStepIndex(bonificacion) {
  if (bonificacion.estado === 'pagado')   return 3;
  if (bonificacion.estado === 'aprobado') return 2;
  if (bonificacion.isr_calculado)         return 1;
  return 0;
}

function StepIndicator({ bonificacion }) {
  const current = getStepIndex(bonificacion);
  return (
    <div className="flex items-center gap-1">
      {STEPS.map((step, i) => {
        const done    = i < current;
        const active  = i === current;
        return (
          <React.Fragment key={step.id}>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap ${
              done   ? 'bg-green-100 text-green-700' :
              active ? 'bg-blue-600 text-white' :
                       'bg-gray-100 text-gray-400'
            }`}>
              {done && <CheckCircle size={9} />}
              {step.label}
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRight size={10} className="text-gray-300 flex-shrink-0" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function DetalleBonificacion({
  bonificacion, detalle, tasaInfotep = 0.005, activeTab, onTabChange,
  onBack, onCalcularISR, onAprobar, onPagar, onEliminar, onEliminarNomina,
  onUpdateDetalle, onRecalcularDesempeno, loading,
}) {
  const esBorrador   = bonificacion.estado === 'borrador';
  const esAprobado   = bonificacion.estado === 'aprobado';
  const isrCalculado = !!bonificacion.isr_calculado;
  const esDesempeno  = bonificacion.metodo_distribucion === 'desempeno';

  const cfg    = ESTADO_CONFIG[bonificacion.estado] || ESTADO_CONFIG.borrador;
  const conPuntajesCero = esDesempeno && detalle.some(d => d.estado !== 'excluido' && !d.puntaje_desempeno);

  const TABS = [
    { id: 'resumen',   label: 'Resumen'   },
    { id: 'empleados', label: 'Empleados', badge: detalle.filter(d => d.estado === 'excluido').length || 0 },
    { id: 'formulas',  label: 'Fórmulas'  },
  ];

  return (
    <div className="space-y-4">

      {/* Top bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={14} /> Volver
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-bold text-gray-900">{bonificacion.nombre}</h2>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
              <cfg.Icon size={10} /> {cfg.label}
            </span>
            <span className="text-xs text-gray-400">
              {TIPO_LABELS[bonificacion.tipo] || bonificacion.tipo} · {bonificacion.anio}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {bonificacion.total_empleados} empleados · RD$ {fmt(bonificacion.total_bonificacion)} total
            {bonificacion.fecha_pago_prevista
              ? <span className="ml-2 text-blue-600 font-semibold">· Período ISR: {new Date(bonificacion.fecha_pago_prevista + 'T00:00:00').toLocaleDateString('es-DO', { month: 'long', year: 'numeric' })}</span>
              : <span className="ml-2 text-red-400 font-medium">· Sin fecha de pago</span>
            }
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => exportXLSX(bonificacion, detalle, tasaInfotep)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
          >
            <Download size={13} /> Excel
          </button>

          {esBorrador && esDesempeno && (
            <button
              onClick={onRecalcularDesempeno}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-violet-200 text-violet-700 rounded-lg hover:bg-violet-50 transition-colors"
            >
              <RotateCcw size={13} /> Recalcular Puntajes
            </button>
          )}

          {esBorrador && !isrCalculado && (
            <button
              onClick={onCalcularISR}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-semibold transition-colors"
            >
              <Calculator size={13} /> Calcular ISR
            </button>
          )}

          {esBorrador && isrCalculado && (
            <button
              onClick={onCalcularISR}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors"
              title="Recalcular ISR"
            >
              <Calculator size={13} /> Recalcular ISR
            </button>
          )}

          {esBorrador && (
            <>
              <button
                onClick={onAprobar}
                disabled={!isrCalculado}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-semibold transition-colors ${
                  isrCalculado
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                title={!isrCalculado ? 'Calcula el ISR primero para poder aprobar' : undefined}
              >
                <CheckCircle size={13} /> Aprobar
              </button>
              <button
                onClick={onEliminar}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 size={13} />
              </button>
            </>
          )}

          {esAprobado && (
            <button
              onClick={onPagar}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors"
            >
              <Lock size={13} /> Marcar Pagado
            </button>
          )}

          {(bonificacion.estado === 'pagado' || bonificacion.estado === 'aprobado') && (
            <button
              onClick={onEliminarNomina}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-orange-200 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors"
              title="Revertir a Generado para recalcular ISR y ajustar"
            >
              <RotateCcw size={13} /> Revertir
            </button>
          )}
        </div>
      </div>

      {/* Step indicator */}
      <StepIndicator bonificacion={bonificacion} />

      {/* Avisos */}
      {esBorrador && !isrCalculado && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-700">
            ISR no calculado. Haz clic en <strong>Calcular ISR</strong> antes de aprobar. El sistema calculará el ISR real a retener considerando el ISR ya pagado en nóminas del año y el saldo a favor del empleado.
          </p>
        </div>
      )}
      {esBorrador && esDesempeno && conPuntajesCero && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-700">
            Hay empleados sin puntaje de desempeño. Ingrésalos en la pestaña Empleados y haz clic en <strong>Recalcular Puntajes</strong>.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex border-b border-gray-200 px-1 pt-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-gray-400 text-white rounded-full">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <>
              {activeTab === 'resumen'   && <ResumenTab bonificacion={bonificacion} detalle={detalle} />}
              {activeTab === 'empleados' && (
                <EmpleadosTab
                  bonificacion={bonificacion}
                  detalle={detalle}
                  tasaInfotep={tasaInfotep}
                  onUpdateDetalle={onUpdateDetalle}
                  editable={esBorrador}
                />
              )}
              {activeTab === 'formulas'  && <FormulasTab bonificacion={bonificacion} />}
            </>
          )}
        </div>
      </div>

    </div>
  );
}
